import {
	EDGER_ORIENTATION_OPTIONS,
	SectionStand,
} from "@/pages/configuration/models/Section";
import { StandTypeChip } from "@/pages/configuration/pages/sections/components/mill-config/StandTypeChip";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	CircularProgress,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { StandWithConfig } from "../models/Stand";
import { getStands } from "../repositories/StandRepository";

export const getStandConfigDetail = (
	stand: Pick<
		SectionStand,
		"StandTypeCode" | "WidthMeasurement" | "EdgerOrientationCode"
	>,
) => {
	if (stand.StandTypeCode === "U")
		return `Width: ${stand.WidthMeasurement ?? ""}`;
	if (stand.StandTypeCode === "E")
		return (
			EDGER_ORIENTATION_OPTIONS.find(
				(o) => o.code === stand.EdgerOrientationCode,
			)?.name ?? ""
		);
	return "";
};

const useStands = ({ sectionId }: { sectionId: number | null }) =>
	useQuery<StandWithConfig[], Error>({
		queryKey: ["standsWithConfig", sectionId],
		queryFn: ({ signal }) => getStands({ sectionId }, signal),
	});

type Props = {
	label?: string;
	sectionId?: number | null;
	value: number | null;
	onChange?: (standId: number | null) => void;
	disabled?: boolean;
	filterStands?: string | null;
	selectedStandTypeCode?: "U" | "E" | null;
};

export const StandSelector = ({
	label = "Stand",
	sectionId = null,
	value,
	onChange = () => {},
	disabled = false,
	filterStands = null,
	selectedStandTypeCode,
}: Props) => {
	const [error, setError] = useState("");

	const {
		data: stands,
		isLoading,
		error: e,
		isError,
	} = useStands({ sectionId });

	const options = (stands ?? []).filter(
		(s) => !filterStands || s.LocationName.startsWith(filterStands),
	);
	const selectedValue = options.find((s) => s.LocationId === value) ?? null;
	const selectedTypeCode =
		selectedStandTypeCode !== undefined
			? selectedStandTypeCode
			: selectedValue?.StandTypeCode;

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Autocomplete
				clearOnEscape
				autoHighlight
				options={options}
				getOptionLabel={(option) => option.LocationName}
				isOptionEqualToValue={(option, v) => option.LocationId === v.LocationId}
				disabled={disabled}
				renderOption={(props, option) => {
					const subtitle = getStandConfigDetail(option);
					return (
						<li {...props} key={option.LocationId}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									width: "100%",
									gap: 2,
								}}
							>
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{option.LocationName}
									</Typography>
									{sectionId != null && subtitle && (
										<Typography
											variant="caption"
											color="textSecondary"
											component="div"
											sx={{ minHeight: "1.66em" }}
										>
											{subtitle}
										</Typography>
									)}
								</Box>
								<StandTypeChip code={option.StandTypeCode} />
							</Box>
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						sx={{
							backgroundColor: (theme) => theme.palette.background.paper,
						}}
						inputProps={{
							...params.inputProps,
							"data-test-id": "stand-selector",
						}}
						InputProps={{
							...params.InputProps,
							startAdornment:
								selectedValue && selectedTypeCode ? (
									<StandTypeChip code={selectedTypeCode} />
								) : null,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedValue}
				onChange={(_, newValue) => onChange(newValue?.LocationId ?? null)}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading stands"
			/>
		</>
	);
};
